import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { 
  ShieldAlert, 
  Loader2,
  Search,
  Filter,
  ChevronRight,
  Eye,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Circle,
  Download,
  FileText,
  FileSpreadsheet
} from "lucide-react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const getTrademarkStatusBadge = (status) => {
  switch (status) {
    case "registered":
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 border border-emerald-200 text-xs font-mono uppercase">Registered</span>;
    case "examination":
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 border border-blue-200 text-xs font-mono uppercase">Examination</span>;
    case "published":
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 border border-purple-200 text-xs font-mono uppercase">Published</span>;
    case "opposed":
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-700 border border-orange-200 text-xs font-mono uppercase">Opposed</span>;
    case "dead":
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-500 border border-slate-200 text-xs font-mono uppercase">Dead</span>;
    default:
      return null;
  }
};

export const Alerts = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [riskFilter, setRiskFilter] = useState("all");
  const [exporting, setExporting] = useState(false);

  const fetchAlerts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (riskFilter !== "all") params.append("risk_level", riskFilter);
      
      const response = await axios.get(`${API}/alerts?${params.toString()}`);
      setAlerts(response.data);
    } catch (error) {
      console.error("Error fetching alerts:", error);
      toast.error("Failed to load alerts");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, riskFilter]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const updateAlertStatus = async (alertId, status) => {
    try {
      await axios.put(`${API}/alerts/${alertId}/status?status=${status}`);
      toast.success("Alert status updated");
      fetchAlerts();
    } catch (error) {
      console.error("Error updating alert:", error);
      toast.error("Failed to update alert");
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (riskFilter !== "all") params.append("risk_level", riskFilter);
      params.append("include_deadlines", "true");
      
      const response = await axios.get(`${API}/alerts/export/${format}?${params.toString()}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `alerts_export.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Alerts exported as ${format.toUpperCase()}`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error(`Failed to export alerts as ${format.toUpperCase()}`);
    } finally {
      setExporting(false);
    }
  };

  const getRiskBadgeClass = (level) => {
    switch (level) {
      case "high": return "badge-high-risk";
      case "medium": return "badge-medium-risk";
      case "low": return "badge-low-risk";
      default: return "badge-safe";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "new": return <AlertTriangle className="w-4 h-4 text-orange-500" />;
      case "reviewed": return <Eye className="w-4 h-4 text-blue-500" />;
      case "dismissed": return <XCircle className="w-4 h-4 text-slate-400" />;
      case "actioned": return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
      default: return null;
    }
  };

  const filteredAlerts = alerts.filter(alert =>
    alert.conflicting_trademark_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    alert.my_trademark_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    alert.conflicting_trademark_applicant.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="alerts-loading">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div data-testid="alerts-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-black text-slate-900 tracking-tight">
            Alerts
          </h1>
          <p className="text-slate-500 mt-1">Potential trademark infringements detected</p>
        </div>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="outline" 
              disabled={exporting || alerts.length === 0}
              data-testid="export-alerts-btn"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Export
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleExport('csv')} data-testid="export-csv-btn">
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Export as CSV
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExport('pdf')} data-testid="export-pdf-btn">
              <FileText className="w-4 h-4 mr-2" />
              Export as PDF
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Filters */}
      <div className="swiss-card mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search alerts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-swiss pl-12"
              data-testid="search-alerts-input"
            />
          </div>
          <div className="flex gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 input-swiss" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="reviewed">Reviewed</SelectItem>
                <SelectItem value="dismissed">Dismissed</SelectItem>
                <SelectItem value="actioned">Actioned</SelectItem>
              </SelectContent>
            </Select>
            <Select value={riskFilter} onValueChange={setRiskFilter}>
              <SelectTrigger className="w-40 input-swiss" data-testid="risk-filter">
                <SelectValue placeholder="Risk Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Risks</SelectItem>
                <SelectItem value="high">High Risk</SelectItem>
                <SelectItem value="medium">Medium Risk</SelectItem>
                <SelectItem value="low">Low Risk</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Alerts List */}
      {filteredAlerts.length === 0 ? (
        <div className="swiss-card text-center py-12" data-testid="no-alerts-message">
          <ShieldAlert className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">
            {searchTerm || statusFilter !== "all" || riskFilter !== "all" 
              ? "No alerts match your filters" 
              : "No alerts detected"
            }
          </p>
          <p className="text-sm text-slate-400 mt-1">
            Run a scan to check for potential trademark infringements
          </p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="alerts-list">
          {filteredAlerts.map((alert, index) => (
            <motion.div
              key={alert.id}
              className={`swiss-card cursor-pointer hover:shadow-md transition-shadow ${
                alert.overall_risk_level === 'high' ? 'border-l-4 border-l-red-500' :
                alert.overall_risk_level === 'medium' ? 'border-l-4 border-l-orange-500' :
                'border-l-4 border-l-emerald-500'
              }`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
              onClick={() => navigate(`/alerts/${alert.id}`)}
              data-testid={`alert-${alert.id}`}
            >
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className={getRiskBadgeClass(alert.overall_risk_level)}>
                      {alert.overall_risk_level} risk
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 text-xs">
                      {getStatusIcon(alert.status)}
                      {alert.status}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                    <div>
                      <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Conflicting Application</p>
                      <p className="font-bold text-slate-900">{alert.conflicting_trademark_name}</p>
                      <p className="text-sm text-slate-500">{alert.conflicting_trademark_applicant}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-xs font-mono text-slate-400">
                          {alert.conflicting_trademark_application_number}
                        </p>
                        {getTrademarkStatusBadge(alert.conflicting_trademark_status)}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Your Trademark</p>
                      <p className="font-bold text-slate-900">{alert.my_trademark_name}</p>
                      <div className="mt-1">
                        {getTrademarkStatusBadge(alert.my_trademark_status)}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-slate-100">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400">Basic Match:</span>
                      <span className={`font-mono font-bold ${
                        alert.basic_similarity_score >= 0.7 ? 'text-red-600' :
                        alert.basic_similarity_score >= 0.5 ? 'text-orange-600' :
                        'text-emerald-600'
                      }`}>
                        {Math.round(alert.basic_similarity_score * 100)}%
                      </span>
                    </div>
                    {alert.ai_similarity_score !== null && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400">AI Match:</span>
                        <span className={`font-mono font-bold ${
                          alert.ai_similarity_score >= 0.7 ? 'text-red-600' :
                          alert.ai_similarity_score >= 0.5 ? 'text-orange-600' :
                          'text-emerald-600'
                        }`}>
                          {Math.round(alert.ai_similarity_score * 100)}%
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400">Filed:</span>
                      <span className="font-mono text-xs text-slate-600">
                        {alert.conflicting_trademark_filing_date}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="btn-secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/alerts/${alert.id}`);
                    }}
                    data-testid={`view-alert-${alert.id}`}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <ChevronRight className="w-5 h-5 text-slate-400 hidden md:block" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};
